// One plain-object Google MapType that owns its own `img` elements, shared by
// all three raster adapters (BBB-457).
//
// WHY IT REPLACED ImageMapType ON EVERY RASTER PATH. `ImageMapType` gives no
// per-tile outcome: its `tilesloaded` event fires whether the tiles arrived or
// not, so on that path a dead provider is indistinguishable from a live one and
// the farmer-facing failure state cannot be built at all. Owning the `img` is
// the price of knowing. The Scottish tile adapter already did exactly this and
// was verified on a live map on 19/08/2026, so the pattern is proven rather
// than hoped for.
//
// What is lost by leaving ImageMapType: Google's own tile fade-in and its
// internal tile cache. What is kept: `getTile`/`releaseTile`/`tileSize` is the
// documented custom overlay contract, opacity is applied on the frame, and
// in-flight requests are cancelled on release.
//
// The `resolve` callback is where each adapter's arithmetic stays: it returns the
// URL plus where to place the image inside the 256px frame, which is what lets
// the Scottish caches draw a stretched ancestor tile past the depth they were
// built to. Returning null is Google's documented "no tile here" and must NOT be
// reported as a failure: nothing was asked for, so nothing can be wrong.

const TILE_PX = 256;

/**
 * @param {object} args
 * @param {string} args.id source id, used as the MapType name
 * @param {(coord: object, zoom: number) => ({url: string, sizePx: number, offsetXPx: number, offsetYPx: number}|null)} args.resolve
 * @param {number} [args.opacity]
 * @param {boolean} [args.pixelated] hard class edges rather than a smeared blend
 * @param {(outcome: 'requested'|'loaded'|'error') => void} [args.onOutcome]
 * @returns {object} a google.maps.MapType
 */
export function createImgTileMapType({ id, resolve, opacity, pixelated = false, onOutcome }) {
  return {
    name: id,
    tileSize: new window.google.maps.Size(TILE_PX, TILE_PX),
    opacity,
    // A custom MapType has to declare its own zoom range: unlike ImageMapType
    // there is no default and Google skips levels outside it. Kept deliberately
    // wide, because what a layer is allowed to draw at is the catalogue's
    // minZoom/maxZoom, enforced in collectDesiredSources.
    minZoom: 0,
    maxZoom: 22,

    getTile(coord, zoom, ownerDocument) {
      const frame = ownerDocument.createElement('div');
      frame.style.width = `${TILE_PX}px`;
      frame.style.height = `${TILE_PX}px`;
      frame.style.overflow = 'hidden';
      frame.style.position = 'relative';
      // A plain MapType has no opacity of its own, unlike ImageMapType, so it is
      // applied here. Without this every layer would draw at full strength and
      // DEFAULT_LAYER_OPACITY would silently stop meaning anything.
      frame.style.opacity = String(opacity ?? 1);

      const placement = resolve(coord, zoom);
      if (!placement) return frame;

      const img = ownerDocument.createElement('img');
      img.style.position = 'absolute';
      img.style.width = `${placement.sizePx}px`;
      img.style.height = `${placement.sizePx}px`;
      img.style.left = `${placement.offsetXPx}px`;
      img.style.top = `${placement.offsetYPx}px`;
      if (pixelated) img.style.imageRendering = 'pixelated';

      img.onload = () => {
        onOutcome?.('loaded');
      };
      // A fused cache holds tiles only where its data is, so a Scotland-only
      // layer 404s over sea and across England and Wales as a matter of course.
      // Hiding the image is all this should ever do: the health rules in
      // lib/layerHealth.js are what decide whether a run of failures means
      // anything, and they need one success to overrule any number of these.
      img.onerror = () => {
        img.style.display = 'none';
        onOutcome?.('error');
      };
      img.alt = '';
      onOutcome?.('requested');
      img.src = placement.url;

      frame.appendChild(img);
      return frame;
    },

    releaseTile(tile) {
      // Cancel any request still in flight and drop the handlers, so panning a
      // long way does not leave a queue of loads for tiles nobody can see, and
      // so a cancelled load cannot report itself as a failure.
      const img = tile?.firstChild;
      if (img) {
        img.onload = null;
        img.onerror = null;
        img.src = '';
      }
    },
  };
}

/** The one-to-one placement: this tile, natural size, no offset. */
export function plainPlacement(url) {
  return url && { url, sizePx: TILE_PX, offsetXPx: 0, offsetYPx: 0 };
}
